import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'aiAdvisory.expandedFolders';

/**
 * EWM-521 §5.9 — Persists per-user folder collapsed/expanded state in localStorage so the
 * sidebar layout survives navigation away from AI Advisory and back. Lightweight by design;
 * folder collapse state is not worth round-tripping to the server.
 */
export const useExpandedFoldersState = () => {
  const [expanded, setExpanded] = useState<Set<string>>(() => {
    if (typeof window === 'undefined') return new Set();
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return new Set();
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed)
        ? new Set(parsed.filter((x) => typeof x === 'string'))
        : new Set();
    } catch {
      return new Set();
    }
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify([...expanded]));
    } catch {
      // localStorage may be unavailable (private mode, quota); ignore — UX still works in-session.
    }
  }, [expanded]);

  const toggle = useCallback((folderId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  }, []);

  // EWM-521 §5.5: idempotent expand used by drag-hover auto-expand — never collapses.
  const expand = useCallback((folderId: string) => {
    setExpanded((prev) => {
      if (prev.has(folderId)) return prev;
      const next = new Set(prev);
      next.add(folderId);
      return next;
    });
  }, []);

  const isExpanded = useCallback((folderId: string) => expanded.has(folderId), [expanded]);

  return { isExpanded, toggle, expand };
};
